import React, { useRef, useState } from "react";
import { IoCloseOutline } from "react-icons/io5";
import axiosOrganizationInstance from "./AxiosOrganizationInstance";
import "./css/create-school.css";

const ADDRESS_SEARCH_DEBOUNCE_MS = 350;

const ORGANIZATION_TYPE_OPTIONS = ["School", "Office"];

const AddOrganization = ({ onClose, createdById, onSuccess, embedded = false }) => {
  const [organizationName, setOrganizationName] = useState("");
  const [organizationCode, setOrganizationCode] = useState("");
  const [organizationType, setOrganizationType] = useState("School");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [specializations, setSpecializations] = useState("");
  const [notes, setNotes] = useState("");
  const [isAdmissions, setIsAdmissions] = useState(false);

  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");
  const [addressSearch, setAddressSearch] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressSearchLoading, setAddressSearchLoading] = useState(false);
  const addressSearchTimeoutRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("Token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleAddressSearchChange = (e) => {
    const value = e.target.value;
    setAddressSearch(value);
    setAddressSuggestions([]);

    if (addressSearchTimeoutRef.current) {
      clearTimeout(addressSearchTimeoutRef.current);
      addressSearchTimeoutRef.current = null;
    }

    if (value.trim().length < 2) return;

    addressSearchTimeoutRef.current = setTimeout(async () => {
      addressSearchTimeoutRef.current = null;
      try {
        setAddressSearchLoading(true);
        const response = await axiosOrganizationInstance.get("/address/suggest", {
          headers: getAuthHeaders(),
          params: { address: value.trim() },
        });
        const list = response.data?.suggestions || response.data?.predictions || [];
        setAddressSuggestions(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error("Address suggest failed:", error?.response?.data || error?.message);
        setAddressSuggestions([]);
      } finally {
        setAddressSearchLoading(false);
      }
    }, ADDRESS_SEARCH_DEBOUNCE_MS);
  };

  const handleAddressSelect = async (placeId) => {
    try {
      const response = await axiosOrganizationInstance.get("/address/suggest/details", {
        headers: getAuthHeaders(),
        params: { placeId },
      });
      const placeDetails = response.data;
      if (!placeDetails) {
        alert("Address details not found");
        return;
      }
      const fullAddress = [
        placeDetails.addressLine1,
        placeDetails.addressLine2,
        placeDetails.city,
        placeDetails.state,
        placeDetails.country,
        placeDetails.postalCode,
      ]
        .filter(Boolean)
        .join(", ");
      setAddressSearch(fullAddress);
      setAddressLine1(placeDetails.addressLine1 || "");
      setAddressLine2(placeDetails.addressLine2 || "");
      setPostalCode(placeDetails.postalCode || "");
      setCity(placeDetails.city || "");
      setState(placeDetails.state || "");
      setCountry(placeDetails.country || "");
      setLat(placeDetails.latitude ?? placeDetails.lat ?? "");
      setLon(placeDetails.longitude ?? placeDetails.lon ?? "");
      setAddressSuggestions([]);
    } catch (error) {
      console.error("Address details failed:", error?.response?.data || error?.message);
      alert("Failed to load address details");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const effectiveCreatedById = createdById || JSON.parse(localStorage.getItem("user") || "{}")?.userId;
    if (!effectiveCreatedById) {
      alert("Current user is missing. Please sign in again.");
      return;
    }

    const phoneDigits = String(phoneNumber || "").replace(/\D/g, "");
    if (phoneDigits.length !== 10) {
      alert("Please enter a valid 10-digit phone number.");
      return;
    }

    const latParsed = lat !== "" && lat != null ? parseFloat(String(lat), 10) : NaN;
    const lonParsed = lon !== "" && lon != null ? parseFloat(String(lon), 10) : NaN;

    const data = {
      organizationCode: organizationCode.trim(),
      organizationName: organizationName.trim(),
      organizationType: organizationType.trim(),
      phoneNumber: phoneDigits,
      email: email.trim(),
      website: website.trim() || undefined,
      notes: notes.trim() || undefined,
      status: "ACTIVE",
      isAdmissions,
      specializations: specializations.trim() || undefined,
      createdById: effectiveCreatedById,
      address: {
        addressLine1: addressLine1.trim(),
        addressLine2: addressLine2.trim() || undefined,
        city: city.trim(),
        state: state.trim(),
        country: country.trim(),
        postalCode: postalCode.trim(),
        ...(Number.isFinite(latParsed) ? { lat: latParsed } : {}),
        ...(Number.isFinite(lonParsed) ? { lon: lonParsed } : {}),
      },
    };

    try {
      const response = await axiosOrganizationInstance.post("/organization", data, {
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      });

      const ok = response.status === 201 || response.status === 200;
      if (!ok) {
        alert("Failed to add organization");
        return;
      }

      const organizationId = response.data?.organizationId || response.data?.organization?.organizationId;
      if (!organizationId) {
        alert("Organization created but organizationId was not returned.");
        return;
      }

      if (typeof onSuccess === "function") {
        onSuccess({
          organization: {
            organizationId,
            organizationName: response.data?.organization?.organizationName || organizationName.trim(),
            organizationCode: response.data?.organization?.organizationCode || organizationCode.trim(),
          },
        });
      }
      if (onClose) onClose();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "An error occurred while adding the organization.";
      alert(errorMessage);
    }
  };

  return (
    <div className={`create-organization-container${embedded ? " create-organization-embedded" : ""}`} style={{ position: "relative" }}>
      {onClose && !embedded ? (
        <button
          type="button"
          onClick={onClose}
          title="Close"
          style={{
            position: "absolute",
            right: 8,
            top: 8,
            border: "none",
            background: "transparent",
            fontSize: 24,
            cursor: "pointer",
          }}
        >
          <IoCloseOutline />
        </button>
      ) : null}
      {!embedded ? <h4 style={{ marginTop: 0 }}>Create Organization</h4> : null}
      <form onSubmit={handleSubmit} className="create-organization-form">
        <div className="row">
          <div className="col-md-4 org-form-field">
            <label className="form-label">Name *</label>
            <input
              className="form-control"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              required
            />
          </div>
          <div className="col-md-4 org-form-field">
            <label className="form-label">Registration Number *</label>
            <input
              className="form-control"
              value={organizationCode}
              onChange={(e) => setOrganizationCode(e.target.value)}
              required
            />
          </div>
          <div className="col-md-4 org-form-field">
            <label className="form-label">Organization Type *</label>
            <select
              className="form-control org-type-select"
              value={organizationType}
              onChange={(e) => setOrganizationType(e.target.value)}
              required
            >
              {ORGANIZATION_TYPE_OPTIONS.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row">
          <div className="col-md-4 org-form-field">
            <label className="form-label">Phone Number *</label>
            <input
              className="form-control"
              inputMode="numeric"
              maxLength={10}
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, "").slice(0, 10))}
              required
            />
          </div>
          <div className="col-md-4 org-form-field">
            <label className="form-label">Email *</label>
            <input
              type="email"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="col-md-4 org-form-field">
            <label className="form-label">Website</label>
            <input
              className="form-control"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-4 org-form-field">
            <label className="form-label">Specialization</label>
            <input
              className="form-control"
              value={specializations}
              onChange={(e) => setSpecializations(e.target.value)}
            />
          </div>
          <div className="col-md-4 org-form-field">
            <label className="form-label">Notes</label>
            <input className="form-control" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div className="col-md-4 org-form-field org-checkbox-field">
            <span className="form-label org-checkbox-heading">Admissions:</span>
            <div className="org-checkbox-label">
              <input
                type="checkbox"
                className="org-checkbox-large"
                checked={isAdmissions}
                onChange={(e) => setIsAdmissions(e.target.checked)}
              />
              <span>Open</span>
            </div>
          </div>
        </div>

        <h5 style={embedded ? undefined : { marginTop: 16 }}>Address</h5>
        <div className="row org-address-search-row">
          <div className="col-md-12 org-address-search-wrap">
            <label className="form-label">Search Address</label>
            <input
              className="form-control"
              value={addressSearch}
              onChange={handleAddressSearchChange}
              placeholder="Type to search address..."
              autoComplete="off"
            />
            {addressSearchLoading ? (
              <small className="org-address-search-loading">Searching...</small>
            ) : null}
            {addressSuggestions.length > 0 ? (
              <ul className="org-address-suggestions">
                {addressSuggestions.map((s, idx) => (
                  <li
                    key={`${s.placeId || s.place_id || idx}`}
                    onClick={() => handleAddressSelect(s.placeId || s.place_id)}
                  >
                    {s.description || s.label || s.formatted_address}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>

        <div className="row">
          <div className="col-md-4">
            <label className="form-label">Address Line 1 *</label>
            <input className="form-control" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} required />
          </div>
          <div className="col-md-4">
            <label className="form-label">Address Line 2</label>
            <input className="form-control" value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} />
          </div>
          <div className="col-md-4">
            <label className="form-label">Postal Code *</label>
            <input className="form-control" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} required />
          </div>
        </div>
        <div className="row">
          <div className="col-md-4">
            <label className="form-label">City *</label>
            <input className="form-control" value={city} onChange={(e) => setCity(e.target.value)} required />
          </div>
          <div className="col-md-4">
            <label className="form-label">State *</label>
            <input className="form-control" value={state} onChange={(e) => setState(e.target.value)} required />
          </div>
          <div className="col-md-4">
            <label className="form-label">Country *</label>
            <input className="form-control" value={country} onChange={(e) => setCountry(e.target.value)} required />
          </div>
        </div>

        <div className="row org-address-coords-row">
          <div className="col-md-4">
            <label className="form-label">Longitude</label>
            <input
              className="form-control"
              type="text"
              value={lon}
              onChange={(e) => setLon(e.target.value)}
              placeholder="Enter longitude"
              readOnly
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Latitude</label>
            <input
              className="form-control"
              type="text"
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              placeholder="Enter latitude"
              readOnly
            />
          </div>
        </div>

        <div className="add-restaurant-submit-button">
          <button type="submit" className="btn btn-primary">
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddOrganization;
